import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useParams, Link } from 'react-router-dom';
import { ImArrowLeft2 } from 'react-icons/im';
import { useDispatch, useSelector } from 'react-redux';
import { FaPlus, FaSortDown } from 'react-icons/fa';
import { format, isSameDay } from 'date-fns';
import { getShiftsListAction, postShiftsFilteredAction } from '../../actions/shift';
import { getUserAction, postUsersFilteredAction } from '../../actions/users';

import Header from '../../components/Header';

import styles from './UserPlanning.module.scss';
import useWindowSize from '../../hooks/useWindowSize';
import SelectDate from '../../components/SelectDate/SelectDate';
import TimeLineVertical from '../../components/TimeLineVertical/TimeLineVertical';
import { IShift, DayType } from '../../types/shift';
import ModalStandalone, { IModalHandle } from '../../lib/ModalStandalone';
import ShiftModal from '../../components/ShiftModal/ShiftModal';
import { SET_USERS_FILTERS } from '../../actions/types';
import DayRow from '../../components/DayRow';
import PlanningRow from '../../components/PlanningRow';
import { postContractsFilteredAction } from '../../actions/contract';
import { getWeekNumber, getActiveContractsBetweenDates } from '../../utils';
import { IUser } from '../../types/user';
import { IContract } from '../../types/contract';

const UserPlanning = () => {
  const userId = useParams()?.userId;
  const dispatch = useDispatch();
  const { width } = useWindowSize();
  const isMobile = width && width < 1044;
  const modalRef = useRef<IModalHandle | any>(null);

  const userAuth = useSelector((store: any) => store.authReducer)?.user;
  const { user, filters, usersList } = useSelector((store: any) => store.usersReducer);
  const { shiftsList, list } = useSelector((store: any) => store.shiftsReducer);
  const { contracts } = useSelector((store: any) => store.contractsReducer);

  const [prevWeek, setPrevWeek] = useState<Date[] | null>();
  const [viewMode, setViewMode] = useState<'my' | 'pole'>('my');

  const displayUser = user || userAuth;

  const today = new Date(new Date().setUTCHours(0, 0, 0, 0));
  const date = new Date(filters.startDate);
  const dayOfWeek = date.getUTCDay();
  const diff = (dayOfWeek === 1) ? 0 : dayOfWeek - 1;
  const monday = new Date(Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate() - (diff === -1 ? 6 : diff),
  ));
  const sunday = new Date(Date.UTC(
    monday.getUTCFullYear(),
    monday.getUTCMonth(),
    monday.getUTCDate() + 6,
  ));
  const lastWeek = new Date(new Date(today).setDate(today.getDate() + 21));
  const last4Week = new Date(new Date(today).setDate(today.getDate() + 28));

  const displayNext = userAuth?.structure?.isNextWeeks
  || (!userAuth?.structure?.isNextWeeks && date.getTime() < lastWeek.getTime());

  const shiftFiltered = useMemo(() => shiftsList.filter((s: IShift) => {
    if (userAuth?.structure?.isNextWeeks) {
      return s;
    }
    if (s.isPublished) {
      return s;
    }
    if (new Date(s.date).getTime() < new Date(last4Week).getTime()) {
      return s;
    }
    return null;
  }), [shiftsList, userAuth?.structure?.isNextWeeks]);

  const dateFormated = filters?.startDate && new Intl.DateTimeFormat('fr', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(filters?.startDate));

  const mondayFormated = filters?.startDate && new Intl.DateTimeFormat('fr', {
    day: 'numeric',
    month: 'long',
  }).format(new Date(filters?.startDate));

  const sundayFormated = filters?.endDate && new Intl.DateTimeFormat('fr', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(filters?.endDate));

  function handleChangeFilters(obj: any) {
    dispatch({
      type: SET_USERS_FILTERS,
      payload: {
        ...filters,
        ...obj,
      },
    });
  }

  function handleStartDate(dir: string) {
    if (!filters?.startDate) {
      return null;
    }
    const startDate = new Date(`${format(new Date(filters.startDate), 'yyyy-MM-dd')}T00:00:00.000Z`);
    const endDate = filters?.endDate ? new Date(`${format(new Date(filters.endDate), 'yyyy-MM-dd')}T00:00:00.000Z`) : null;
    if (endDate) {
      if (dir === 'prev') {
        startDate.setUTCDate(startDate.getUTCDate() - 7);
        endDate.setUTCDate(endDate.getUTCDate() - 7);
      } else {
        startDate.setUTCDate(startDate.getUTCDate() + 7);
        endDate.setUTCDate(endDate.getUTCDate() + 7);
      }
    } else if (dir === 'prev') {
      startDate.setUTCDate(startDate.getUTCDate() - 1);
    } else {
      startDate.setUTCDate(startDate.getUTCDate() + 1);
    }
    return handleChangeFilters({
      startDate: startDate.toISOString(),
      endDate: endDate ? endDate.toISOString() : null,
    });
  }

  useEffect(() => {
    if (user?.structure?._id) {
      getShiftsListAction(dispatch, user?.structure?._id);
    }
  }, [user?.structure?._id]);

  useEffect(() => {
    if (userId) {
      getUserAction(dispatch, userId);
    }
  }, [userId]);

  useEffect(() => {
    if (!filters?.startDate) return;
    const payload = userAuth?.role === 'Member' && viewMode === 'pole'
      ? { ...filters, view: 'pole' }
      : { ...filters };
    if (userAuth?.role === 'Member' && payload.view !== 'pole') {
      delete (payload as any).view;
    }
    postShiftsFilteredAction(dispatch, payload);
    postContractsFilteredAction(dispatch, filters);
  }, [filters, viewMode, userAuth?.role]);

  useEffect(() => {
    if (userAuth?.role === 'Member' && viewMode === 'pole' && userAuth?.poles?.length) {
      const structureId = typeof userAuth?.structure === 'object'
        ? (userAuth.structure as { _id?: string })?._id
        : userAuth?.structure;
      postUsersFilteredAction(dispatch, {
        structure: structureId || filters.structure,
        poles: userAuth.poles,
        search: '',
      });
    }
  }, [userAuth?.role, viewMode, userAuth?.poles, userAuth?.structure, filters.structure]);

  useEffect(() => {
    let structureId: string | undefined;
    if (userAuth?.role === 'Member' && userAuth?.structure) {
      structureId = typeof userAuth.structure === 'object'
        ? (userAuth.structure as { _id?: string })?._id
        : userAuth.structure;
    }
    const updates: Record<string, unknown> = !isMobile
      ? { startDate: monday.toISOString(), endDate: sunday.toISOString() }
      : { startDate: filters?.startDate ?? monday.toISOString(), endDate: null };
    if (structureId && !filters?.structure) {
      updates.structure = structureId;
    }
    handleChangeFilters(updates);
  }, [isMobile, userAuth?.role, userAuth?.structure]);

  useEffect(() => {
    if (!isMobile) {
      if (filters?.startDate) {
        const start = new Date(filters.startDate);
        const daysOptions : Date[] = [];
        let current = 0;
        const end = 6;
        while (current <= end) {
          const newDate = new Date(Date.UTC(
            start.getUTCFullYear(),
            start.getUTCMonth(),
            start.getUTCDate() + current,
          ));
          daysOptions.push(newDate);
          current += 1;
        }
        setPrevWeek(daysOptions);
      }
    }
  }, [filters?.startDate]);

  useEffect(() => {
    if (userAuth?.role === 'Member' && !filters.structure && userAuth?.structure) {
      const structureId = typeof userAuth.structure === 'object'
        ? (userAuth.structure as { _id?: string })?._id
        : userAuth.structure;
      if (structureId) {
        handleChangeFilters({ structure: structureId });
      }
    }
  }, [userAuth?.role, userAuth?.structure, filters.structure]);

  const poleUsers = useMemo(() => {
    if (!usersList?.length || !userAuth?.poles?.length) return [];
    const hasMatchingPole = (u: IUser) => u.poles?.some((p: string) => userAuth.poles?.includes(p));
    return usersList.filter(hasMatchingPole);
  }, [usersList, userAuth?.poles]);

  const weekForPole: DayType[] = useMemo(() => {
    if (!prevWeek?.length) return [];
    return prevWeek.map((d: Date) => ({
      date: d.toISOString(),
      day: d.getUTCDate(),
      weekDay: new Intl.DateTimeFormat('fr', { weekday: 'long', day: 'numeric' }).format(d),
    }));
  }, [prevWeek]);

  const activesContracts = useMemo(() => getActiveContractsBetweenDates(
    contracts || [],
    filters.startDate,
    filters?.endDate || filters.startDate,
  ), [contracts, filters.startDate, filters?.endDate]);

  const weekForMobileDay: DayType[] = useMemo(() => [], []);

  return (
    <div className={`${styles.container} ${userAuth.role === 'Member' ? styles.member : ''}`}>
      <Header name={userAuth.role !== 'Member' ? '' : 'Planning'} isProfile />
      {userAuth.role !== 'Member'
      && <>
        <Link
          className={styles.back}
          to={'/admin/planning'}
        >
          <ImArrowLeft2 /> <p>RETOUR AU Planning</p>
        </Link>
        </>
      }
      <div className={styles.content}>
      {userAuth.role !== 'Member'
        && <>
          <div className={styles.user}>
            <div className={styles.picture}>
              {user?.picture?.path
                ? <img src={`${process.env.REACT_APP_API_URL}/public/${user.picture.path}`} alt={user.picture.alt}/>
                : <p>
                  {user?.profile?.firstName[0]}.{user?.profile?.lastName[0]}
                </p>
              }
            </div>
            <p className={styles.name}>{user?.profile?.firstName} {user?.profile?.lastName}</p>
          </div>
          </>
        }
        <div className={styles.filters}>
          {isMobile
            && <div className={styles.row}>
              <div className={styles['select-date-mobile']}>
                <SelectDate />
              </div>
            </div>
          }
        </div>
        <div className={styles['mobile-filters']}>
          <h4>{dateFormated}</h4>
          {userAuth.role === 'Member' && userAuth?.poles?.length > 0
            && (
              <div className={styles.viewToggle}>
                <button
                  type="button"
                  className={viewMode === 'my' ? styles.active : ''}
                  onClick={() => setViewMode('my')}
                >
                  Mon planning
                </button>
                <button
                  type="button"
                  className={viewMode === 'pole' ? styles.active : ''}
                  onClick={() => setViewMode('pole')}
                >
                  Planning de mon pôle
                </button>
              </div>
            )}
        </div>
        {isMobile && (
          <div className={styles.list}>
            {userAuth.role === 'Member' && viewMode === 'pole' && poleUsers.length > 0
              ? poleUsers
                .filter((u: IUser) => activesContracts.some(
                  (c: IContract) => c.user === u._id,
                ))
                .map((u: IUser) => (
                  <PlanningRow
                    key={u._id}
                    user={u}
                    isMobile
                    week={weekForMobileDay}
                    shifts={
                      shiftFiltered?.filter(
                        (s: IShift) => s.user === u._id
                          && isSameDay(new Date(s.date), new Date(filters.startDate)),
                      ) || []
                    }
                  />
                ))
              : (
                <TimeLineVertical
                  shifts={shiftFiltered}
                />
              )}
          </div>
        )}
        {!isMobile && shiftsList && prevWeek && prevWeek?.length > 0
          && <div className={styles.week}>
            <div className={styles.title}>
              <div className={styles['select-date']}>
                <button
                  type='button'
                  onClick={() => handleStartDate('prev')}
                >
                  <FaSortDown />
                </button>
                <h4>{`${mondayFormated} > ${sundayFormated}`}<br/>
                  Semaine {getWeekNumber(sunday)}
                </h4>
                {displayNext
                  && <button
                  type='button'
                  onClick={() => handleStartDate('next')}
                >
                  <FaSortDown />
                </button>
                }
              </div>
              {userAuth.role === 'Member' && userAuth?.poles?.length > 0
                && (
                  <div className={styles.viewToggle}>
                    <button
                      type="button"
                      className={viewMode === 'my' ? styles.active : ''}
                      onClick={() => setViewMode('my')}
                    >
                      Mon planning
                    </button>
                    <button
                      type="button"
                      className={viewMode === 'pole' ? styles.active : ''}
                      onClick={() => setViewMode('pole')}
                    >
                      Planning de mon pôle
                    </button>
                  </div>
                )}
            </div>
            <div className={styles.labels}>
              <div className={styles.row}>
              </div>
              <div className={styles.row}>
                {list?.shifts?.map((s : any) => {
                  const structureSchedules = (displayUser?.structure) as
                    { schedules?: { end?: string } } | undefined;
                  const label = s.label?.includes(':00') && s.label !== '02:00'
                    && s.label !== structureSchedules?.schedules?.end ? s.label : null;
                  return (
                  <div key={s.label}
                    style={{ width: `${100 / list.shifts.length}%` }}
                    className={styles.hour}
                  >
                    <p>{label}</p>
                  </div>
                  );
                })}
              </div>
            </div>
            <div className={styles.list}>
              {userAuth.role === 'Member' && viewMode === 'pole' && weekForPole.length > 0
                ? (
                  <div className={styles.grid}>
                    {poleUsers
                      .filter((u: IUser) => activesContracts.some(
                        (c: IContract) => c.user === u._id,
                      ))
                      .map((u: IUser) => (
                        <PlanningRow
                          key={u._id}
                          user={u}
                          isMobile={!!isMobile}
                          week={weekForPole}
                          shifts={
                            shiftFiltered?.filter((s: IShift) => s.user === u._id) || []
                          }
                        />
                      ))}
                  </div>
                )
                : (
                  <div className={styles.days}>
                    {prevWeek?.map((d: Date) => <DayRow
                      user={displayUser}
                      key={d.toISOString()}
                      shifts={shiftFiltered?.filter(
                        (s: IShift) => isSameDay(new Date(s.date), d)
                          && s.user === displayUser?._id,
                      )}
                      day={d}
                      isToday={d.toISOString() === today.toISOString()}
                    />)}
                  </div>
                )}
            </div>
          </div>
        }
      </div>
      {userAuth.role !== 'Member'
      && <>
      <button
        type="button"
        className={styles.new}
        onClick={() => modalRef.current.open()}
      >
        <div className={styles.icon}>
          <FaPlus />
        </div>
      </button>
      <ModalStandalone ref={modalRef} isDraggable={false}>
        <ShiftModal
          user={user}
        />
      </ModalStandalone>
      </>
      }
    </div>
  );
};

export default UserPlanning;
